import { isNonNaNNumber, Milliseconds } from '@iotv/iotv-v3-types';
import { NavSections } from '../../navigation/NavSections';
import { AppLocalStorage } from '../../../data/LocalStorage/AppLocalStorage';
import { createContext } from 'react';
export const isPointInTimeObservationInput = (o) => o.observationType === 'POINT_IN_TIME';
export const isCompletePointInTimeObservationInput = (o) => isPointInTimeObservationInput(o) && isNonNaNNumber(o.timestamp);
export const isPeriodObservationInput = (o) => o.observationType === 'DEFINED_PERIOD';
export const isCompletePeriodObservationInput = (o) => isPeriodObservationInput(o) && [o.periodBasisMs, o.fromTimestampMs, o.toTimestampMs].every((v) => isNonNaNNumber(v));
export const getNullMapFromKeys = (keys) => Object.fromEntries(keys.map((k) => [k, null]));
export const getNullMap = (kv) => getNullMapFromKeys(Object.keys(kv));
export const getNextObservationInput = (currentObservationInput) => {
    const newState = getNullMap(currentObservationInput.state);
    if (isCompletePointInTimeObservationInput(currentObservationInput)) {
        const { observationType, timestamp, fromTimestampMs, toTimestampMs, state, description } = currentObservationInput;
        const diff = toTimestampMs - fromTimestampMs;
        const noZeroDiff = diff === 0 ? Milliseconds.MINUTE * 5 : diff;
        return {
            state: newState,
            timestamp: Date.now(),
            observationType,
            fromTimestampMs: fromTimestampMs + noZeroDiff,
            toTimestampMs: toTimestampMs + noZeroDiff,
            description: null,
        };
    }
    else if (isCompletePeriodObservationInput(currentObservationInput)) {
        const { observationType, timestamp, fromTimestampMs, toTimestampMs, state, description, periodBasisMs } = currentObservationInput;
        const noZeroPeriodBasisMs = periodBasisMs === 0 ? Milliseconds.MINUTE * 5 : periodBasisMs;
        return {
            state: newState,
            observationType,
            periodBasisMs: noZeroPeriodBasisMs,
            timestamp: Date.now(),
            fromTimestampMs: fromTimestampMs + noZeroPeriodBasisMs,
            toTimestampMs: toTimestampMs + noZeroPeriodBasisMs,
            description: null,
        };
    }
    else {
        // never
        console.log('WARN: got to getNextObservationInput with incomplete input,', { currentObservationInput });
        // throw new Error('ObservationInput is not complete, cannot get next observation input')
        const { fromTimestampMs = Date.now(), toTimestampMs = Date.now(), ...base } = currentObservationInput;
        const diff = (toTimestampMs ?? 0) - (fromTimestampMs ?? 0);
        const noZeroDiff = diff === 0 ? Milliseconds.MINUTE * 5 : diff;
        return { ...base, observationType: 'POINT_IN_TIME', fromTimestampMs: (fromTimestampMs ?? 0) + noZeroDiff, toTimestampMs: (toTimestampMs ?? 0) + noZeroDiff, state: newState, description: null };
    }
};
export const getIniitialPointInTimeObservationInput = (keys) => ({
    observationType: 'POINT_IN_TIME',
    timestamp: null,
    fromTimestampMs: null,
    toTimestampMs: null,
    state: getNullMapFromKeys(keys ?? []),
    description: null,
});
export const getInitialPeriodObservationInput = (keys) => ({
    observationType: 'DEFINED_PERIOD',
    timestamp: null,
    fromTimestampMs: null,
    toTimestampMs: null,
    periodBasisMs: null,
    state: getNullMapFromKeys(keys ?? []),
    description: null,
});
const isReInitializeObservationInputAction = (action) => action.type === 'reInitialize';
const isSetBasisAction = (action) => action.type === 'setIsBasis';
const isAddObservationInputAction = (action) => action.type === 'addObservationInput';
const isChangeCurrentObservationInputAction = (action) => action.type === 'changeCurrentObservationInput';
const isChangeObservationInputKeysAction = (action) => action.type === 'changeObservationInputKeys';
export const ObservationInputReducer = (currentState, action) => {
    console.log(`ObservationInputReducer got action`, { action, currentState });
    const update = { ...currentState };
    if (isReInitializeObservationInputAction(action)) {
        const { value } = action;
        Object.assign(update, value);
    }
    else if (isSetBasisAction(action)) {
        const { value } = action;
        if (update.currentObservationInput === null) {
            //presumably don't do anything
        }
        else {
            const { lastObservationInput, observationType, periodBasisMs } = value;
            update.periodBasisMs = periodBasisMs;
            if (observationType === 'DEFINED_PERIOD') {
                //ignored
            }
            update.currentObservationInput.observationType = observationType;
            if (isPeriodObservationInput(update.currentObservationInput) && observationType === 'DEFINED_PERIOD') {
                update.currentObservationInput.periodBasisMs = periodBasisMs;
            }
            else if (isPointInTimeObservationInput(update.currentObservationInput) && observationType === 'POINT_IN_TIME') {
                delete update.currentObservationInput.periodBasisMs;
            }
            if (periodBasisMs) {
                update.currentObservationInput.toTimestampMs = (update.currentObservationInput.fromTimestampMs ?? Date.now()) + periodBasisMs;
            }
        }
    }
    else if (isAddObservationInputAction(action)) {
        const { value } = action;
        update.lastObservationInput = update.currentObservationInput;
        update.currentObservationInput = getNextObservationInput(value.observationInput);
        // Handle addObservationInput action
    }
    else if (isChangeCurrentObservationInputAction(action)) {
        const { value } = action;
        update.currentObservationInput = value.observationInput;
        // Handle changeCurrentObservationInput action
    }
    else if (isChangeObservationInputKeysAction(action)) {
        const { value } = action;
        update.observationKeys = value.observationKeys;
        if (update.currentObservationInput) {
            update.currentObservationInput.state = getNullMapFromKeys(value.observationKeys);
        }
        // Handle changeObservationInputKeys action
    }
    const currentInputIsValid = update.currentObservationInput &&
        (isCompletePeriodObservationInput(update.currentObservationInput) || isCompletePointInTimeObservationInput(update.currentObservationInput));
    update.currentInputIsValid = !!currentInputIsValid;
    console.log(`Temp ObservationInputReducer returning`, update);
    return update;
};
export const DefaultObservationInputState = {
    observationKeys: null,
    lastObservationInput: null,
    currentObservationInput: null,
    currentInputIsValid: false,
    periodBasisMs: AppLocalStorage.get(NavSections.THINGVIEW, 'observationsPeriodBasisMs') ?? Milliseconds.MINUTE * 5,
};
export const ObservationInputContext = createContext({
    state: DefaultObservationInputState,
    dispatch: () => { },
});
